import { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { TouchBackend } from 'react-dnd-touch-backend';
import { Icon } from '@iconify/react';
import edit2Fill from '@iconify/icons-eva/edit-2-fill';
import trash2Outline from '@iconify/icons-eva/trash-2-outline';
import plusFill from '@iconify/icons-eva/plus-fill';
import moveFill from '@iconify/icons-eva/move-fill';
import RichTextEditor from 'components/kanban/RichTextEditor';
import VariablePalette from './VariablePalette';

const ITEM_TYPE = 'CLAUSE_BLOCK';

const getBackend = () => {
  if (typeof window === 'undefined') return HTML5Backend;
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  return isTouchDevice ? TouchBackend : HTML5Backend;
};

const newBlock = () => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
  title: '',
  content: '',
  enabled: true,
});

const BlockRow = ({ block, index, moveBlock, onEdit, onDelete, onToggle, readOnly }) => {
  const ref = useRef(null);
  const [, drop] = useDrop({
    accept: ITEM_TYPE,
    hover(item) {
      if (readOnly) return;
      if (!ref.current) return;
      if (item.index === index) return;
      moveBlock(item.index, index);
      item.index = index;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: ITEM_TYPE,
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    canDrag: () => !readOnly,
  });

  drag(drop(ref));

  return (
    <Card
      ref={ref}
      variant="outlined"
      sx={{
        opacity: isDragging ? 0.5 : 1,
        borderStyle: 'dashed',
        borderColor: block.enabled ? 'divider' : 'warning.light',
      }}
    >
      <CardContent sx={{ py: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Tooltip title="Arrastrar">
            <IconButton size="small">
              <Icon icon={moveFill} />
            </IconButton>
          </Tooltip>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2">{block.title || 'Cláusula sin título'}</Typography>
            <Typography variant="caption" color="text.secondary">
              {block.enabled ? 'Activa' : 'Inactiva'}
            </Typography>
          </Box>
          <Switch size="small" checked={block.enabled} onChange={(event) => onToggle(index, event.target.checked)} disabled={readOnly} />
          <Tooltip title="Editar">
            <IconButton size="small" onClick={() => onEdit(index)} disabled={readOnly}>
              <Icon icon={edit2Fill} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar">
            <IconButton size="small" onClick={() => onDelete(index)} disabled={readOnly}>
              <Icon icon={trash2Outline} />
            </IconButton>
          </Tooltip>
        </Stack>
      </CardContent>
    </Card>
  );
};

BlockRow.propTypes = {
  block: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  moveBlock: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onToggle: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
};

const ClauseBlocksEditor = ({ value, onChange, readOnly = false, variables = [] }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [draft, setDraft] = useState(newBlock());
  const editorRef = useRef(null);

  const blocks = Array.isArray(value) ? value : [];

  const openNew = () => {
    if (readOnly) return;
    setDraft(newBlock());
    setEditIndex(null);
    setModalOpen(true);
  };

  const openEdit = (index) => {
    if (readOnly) return;
    setDraft({ ...blocks[index] });
    setEditIndex(index);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  const saveBlock = () => {
    if (readOnly) return;
    if (!draft.title.trim()) return;
    if (editIndex === null) {
      onChange([...blocks, draft]);
    } else {
      const next = blocks.map((item, idx) => (idx === editIndex ? draft : item));
      onChange(next);
    }
    closeModal();
  };

  const removeBlock = (index) => {
    if (readOnly) return;
    const next = blocks.filter((_, idx) => idx !== index);
    onChange(next);
  };

  const toggleBlock = (index, enabled) => {
    if (readOnly) return;
    const next = blocks.map((item, idx) => (idx === index ? { ...item, enabled } : item));
    onChange(next);
  };

  const moveBlock = (from, to) => {
    if (readOnly) return;
    const next = [...blocks];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onChange(next);
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="subtitle1">Cláusulas</Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={<Icon icon={plusFill} />}
          onClick={openNew}
          disabled={readOnly}
        >
          Nueva cláusula
        </Button>
      </Stack>

      <DndProvider backend={getBackend()} options={{ enableMouseEvents: true }}>
        <Stack spacing={1.5}>
          {blocks.map((block, index) => (
            <BlockRow
              key={block.id}
              block={block}
              index={index}
              moveBlock={moveBlock}
              onEdit={openEdit}
              onDelete={removeBlock}
              onToggle={toggleBlock}
              readOnly={readOnly}
            />
          ))}
          {!blocks.length && (
            <Typography variant="body2" color="text.secondary">
              Agrega cláusulas para construir el contrato.
            </Typography>
          )}
        </Stack>
      </DndProvider>

      <Dialog open={modalOpen} onClose={closeModal} maxWidth="xl" fullWidth>
        <DialogTitle>{editIndex === null ? 'Nueva cláusula' : 'Editar cláusula'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1, minHeight: 520 }}>
            <TextField
              fullWidth
              label="Título"
              value={draft.title}
              onChange={(event) => setDraft((prev) => ({ ...prev, title: event.target.value }))}
              disabled={readOnly}
            />
            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <RichTextEditor
                  ref={editorRef}
                  value={draft.content}
                  onChange={(html) => setDraft((prev) => ({ ...prev, content: html }))}
                  placeholder="Redacta la cláusula"
                  readOnly={readOnly}
                  variables={variables}
                />
              </Box>
              <Box sx={{ width: { xs: '100%', lg: 360 } }}>
                <VariablePalette
                  variables={variables}
                  readOnly={readOnly}
                  onInsert={(key) => editorRef.current?.insertVariable?.(key)}
                  height={360}
                />
              </Box>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModal}>Cancelar</Button>
          <Button variant="contained" onClick={saveBlock} disabled={readOnly}>Guardar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

ClauseBlocksEditor.propTypes = {
  value: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
  variables: PropTypes.array,
};

export const buildContractHtmlFromBlocks = (introHtml = '', blocks = []) => {
  const enabledBlocks = blocks.filter((block) => block.enabled !== false);
  const sections = enabledBlocks.map((block) => (
    `<section style="margin-bottom:16px;"><h3>${block.title}</h3>${block.content || ''}</section>`
  ));
  const introSection = introHtml ? `<section style="margin-bottom:16px;">${introHtml}</section>` : '';
  return [introSection, ...sections].join('');
};

export default ClauseBlocksEditor;
